import { useState, useMemo, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/AppLayout";
import { PhoneCalendarButton } from "@/components/PhoneCalendarButton";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Phone, CalendarDays, CheckSquare, AlertCircle, Plus, Trash2, Sun, Clock, Sparkles, User, AlarmClock, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { fmtDateTime } from "@/lib/constants";
import ActivityDetailDialog from "@/components/ActivityDetailDialog";

const ICON = { call: Phone, meeting: CalendarDays, task: CheckSquare };
const PRIORITY_DOT = { high: "bg-rose-500", medium: "bg-amber-500", low: "bg-slate-300" };
const PRIORITY_RANK = { high: 0, medium: 1, low: 2 };
const PRIORITY_BADGE = {
  high: "border-rose-300 bg-rose-50 text-rose-700",
  medium: "border-amber-300 bg-amber-50 text-amber-800",
  low: "border-slate-300 bg-slate-50 text-slate-600",
};

function isOverdue(a) { return a.due_at && a.due_at < new Date().toISOString(); }
function todayEndIso() { const d = new Date(); d.setHours(23, 59, 59, 999); return d.toISOString(); }
function weekEndIso() { const d = new Date(); d.setDate(d.getDate() + 7); d.setHours(23, 59, 59, 999); return d.toISOString(); }
const inHours = (h) => new Date(Date.now() + h * 3600000).toISOString();
const todayAt = (h) => { const d = new Date(); d.setHours(h, 0, 0, 0); if (d < new Date()) d.setDate(d.getDate() + 1); return d.toISOString(); };
const daysAt = (days, h) => { const d = new Date(); d.setDate(d.getDate() + days); d.setHours(h, 0, 0, 0); return d.toISOString(); };

const SORT_TASK = (a, b) => {
  const p = PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority];
  if (p !== 0) return p;
  return (a.due_at || "").localeCompare(b.due_at || "");
};

export default function DailyTasksPage() {
  const nav = useNavigate();
  const [selTask, setSelTask] = useState(null);
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = ["admin", "super_admin"].includes(user?.role);
  const [showCompleted, setShowCompleted] = useState(false);
  const [assigneeFilter, setAssigneeFilter] = useState("all");
  const [quickTitle, setQuickTitle] = useState("");
  const [quickPriority, setQuickPriority] = useState("medium");
  const [quickDue, setQuickDue] = useState("today"); // today | tomorrow | week
  const [quickAssignee, setQuickAssignee] = useState("me");
  const inputRef = useRef(null);

  const { data: team = [] } = useQuery({
    queryKey: ["team"],
    queryFn: async () => (await api.get("/auth/team")).data,
  });

  const { data: activities = [], isFetching } = useQuery({
    queryKey: ["daily-tasks", showCompleted, assigneeFilter, isAdmin],
    queryFn: async () => {
      const params = {};
      if (!showCompleted) params.completed = false;
      if (isAdmin) params.assignee = assigneeFilter;
      return (await api.get("/activities", { params })).data;
    },
  });

  const add = useMutation({
    mutationFn: async () => {
      const now = new Date();
      let due = new Date();
      if (quickDue === "today") due.setHours(17, 0, 0, 0);
      if (quickDue === "tomorrow") { due.setDate(due.getDate() + 1); due.setHours(9, 0, 0, 0); }
      if (quickDue === "week") { due.setDate(due.getDate() + 7); due.setHours(9, 0, 0, 0); }
      // Never schedule in the past for "today"
      if (due < now) due = now;
      return (await api.post("/activities", {
        type: "task",
        title: quickTitle.trim(),
        priority: quickPriority,
        due_at: due.toISOString(),
        assigned_to: quickAssignee === "me" ? user?.id : quickAssignee,
        completed: false,
      })).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["daily-tasks"] });
      qc.invalidateQueries({ queryKey: ["activities"] });
      qc.invalidateQueries({ queryKey: ["activities-today"] });
      setQuickTitle("");
      toast.success("Task added");
      inputRef.current?.focus();
    },
  });

  const toggle = useMutation({
    mutationFn: async ({ id, completed }) => (await api.patch(`/activities/${id}`, { completed })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["daily-tasks"] });
      qc.invalidateQueries({ queryKey: ["activities-today"] });
      qc.invalidateQueries({ queryKey: ["activities"] });
      qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
    },
  });
  const reprioritize = useMutation({
    mutationFn: async ({ id, priority }) => (await api.patch(`/activities/${id}`, { priority })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["daily-tasks"] }),
  });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/activities/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["daily-tasks"] }); toast.success("Deleted"); },
  });
  const [remindTask, setRemindTask] = useState(null);
  const [remindAt, setRemindAt] = useState("");
  const remind = useMutation({
    mutationFn: async ({ id, due_at }) => (await api.patch(`/activities/${id}`, { due_at, completed: false })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["daily-tasks"] });
      qc.invalidateQueries({ queryKey: ["activities"] });
      qc.invalidateQueries({ queryKey: ["activities-today"] });
      toast.success("Reminder set");
    },
  });

  const buckets = useMemo(() => {
    const now = new Date().toISOString();
    const today = todayEndIso();
    const week = weekEndIso();
    const overdue = [];
    const dueToday = [];
    const thisWeek = [];
    const later = [];
    const done = [];
    for (const a of activities) {
      if (a.completed) { done.push(a); continue; }
      if (!a.due_at) { later.push(a); continue; }
      if (a.due_at < now) overdue.push(a);
      else if (a.due_at <= today) dueToday.push(a);
      else if (a.due_at <= week) thisWeek.push(a);
      else later.push(a);
    }
    [overdue, dueToday, thisWeek, later].forEach((b) => b.sort(SORT_TASK));
    return { overdue, dueToday, thisWeek, later, done };
  }, [activities]);

  const totalOpen = buckets.overdue.length + buckets.dueToday.length + buckets.thisWeek.length + buckets.later.length;

  return (
    <div>
      <PageHeader
        title="Daily Tasks"
        subtitle={isAdmin ? "Everyone's to-do list — filter per team member, or see the lot." : "Your to-do list. Tick as you go — done tasks flow back into every deal, quote and client record they came from."}
        breadcrumb={["Sales", "Daily Tasks"]}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <PhoneCalendarButton />
            <Button data-testid="tasks-view-activities" onClick={() => nav("/activities")} variant="outline" className="rounded-sm h-10">
              View all activities
            </Button>
          </div>
        }
      />

      <div className="px-6 lg:px-10 py-6 max-w-5xl mx-auto space-y-5">
        {/* Admin: whose tasks am I looking at? */}
        {isAdmin && (
          <div className="flex items-center gap-2">
            <User size={14} className="text-slate-500" />
            <div className="stamp-label text-slate-500">Showing tasks for</div>
            <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
              <SelectTrigger data-testid="assignee-filter" className="rounded-sm h-9 w-48"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Everyone</SelectItem>
                <SelectItem value="me">Just me</SelectItem>
                {team.filter((m) => m.id !== user?.id).map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Stat strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard testid="stat-overdue" label="Overdue" value={buckets.overdue.length} tone="rose" Icon={AlertCircle} />
          <StatCard testid="stat-today" label="Due Today" value={buckets.dueToday.length} tone="cyan" Icon={Sun} />
          <StatCard testid="stat-week" label="This Week" value={buckets.thisWeek.length} tone="sky" Icon={Clock} />
          <StatCard testid="stat-done" label="Completed" value={buckets.done.length} tone="emerald" Icon={CheckSquare} />
        </div>

        {/* Quick-add */}
        <Card className="rounded-sm p-4 industrial-shadow border-l-4 border-l-cyan-500">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={14} className="text-cyan-600" />
            <div className="stamp-label text-slate-600">Quick add</div>
          </div>
          <div className="flex flex-col md:flex-row gap-2">
            <Input
              ref={inputRef}
              data-testid="quick-add-input"
              value={quickTitle}
              onChange={(e) => setQuickTitle(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && quickTitle.trim() && add.mutate()}
              placeholder="What needs doing?"
              className="rounded-sm h-11 flex-1"
              autoFocus
            />
            <Select value={quickDue} onValueChange={setQuickDue}>
              <SelectTrigger data-testid="quick-due-select" className="rounded-sm h-11 w-full md:w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="tomorrow">Tomorrow</SelectItem>
                <SelectItem value="week">This week</SelectItem>
              </SelectContent>
            </Select>
            <Select value={quickPriority} onValueChange={setQuickPriority}>
              <SelectTrigger data-testid="quick-priority-select" className="rounded-sm h-11 w-full md:w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="high">🔴 High</SelectItem>
                <SelectItem value="medium">🟡 Medium</SelectItem>
                <SelectItem value="low">⚪ Low</SelectItem>
              </SelectContent>
            </Select>
            <Select value={quickAssignee} onValueChange={setQuickAssignee}>
              <SelectTrigger data-testid="quick-assignee-select" className="rounded-sm h-11 w-full md:w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="me">Assign: Me</SelectItem>
                {team.filter((m) => m.id !== user?.id).map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button data-testid="quick-add-btn" onClick={() => add.mutate()} disabled={!quickTitle.trim() || add.isPending} className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm h-11 px-6">
              <Plus size={14} className="mr-1.5" /> Add
            </Button>
          </div>
        </Card>

        {/* Sections */}
        <TaskSection
          onOpen={setSelTask}
          testid="section-overdue"
          title="Overdue"
          icon={AlertCircle}
          tone="rose"
          tasks={buckets.overdue}
          emptyText="Nothing overdue — well done."
          onToggle={toggle.mutate}
          onPriority={reprioritize.mutate}
          onDelete={del.mutate}
          onRemind={remind.mutate}
          onCustom={(t) => { setRemindTask(t); setRemindAt(""); }}
          onNav={nav}
        />
        <TaskSection
          onOpen={setSelTask}
          testid="section-today"
          title="Due Today"
          icon={Sun}
          tone="cyan"
          tasks={buckets.dueToday}
          emptyText="Free schedule today — knock out something from This Week."
          onToggle={toggle.mutate}
          onPriority={reprioritize.mutate}
          onDelete={del.mutate}
          onRemind={remind.mutate}
          onCustom={(t) => { setRemindTask(t); setRemindAt(""); }}
          onNav={nav}
        />
        <TaskSection
          onOpen={setSelTask}
          testid="section-week"
          title="This Week"
          icon={Clock}
          tone="sky"
          tasks={buckets.thisWeek}
          emptyText="No tasks in the next 7 days."
          onToggle={toggle.mutate}
          onPriority={reprioritize.mutate}
          onDelete={del.mutate}
          onRemind={remind.mutate}
          onCustom={(t) => { setRemindTask(t); setRemindAt(""); }}
          onNav={nav}
        />
        {buckets.later.length > 0 && (
          <TaskSection
          onOpen={setSelTask}
            testid="section-later"
            title="Later / Undated"
            icon={CalendarDays}
            tone="slate"
            tasks={buckets.later}
            onToggle={toggle.mutate}
            onPriority={reprioritize.mutate}
            onDelete={del.mutate}
          onRemind={remind.mutate}
          onCustom={(t) => { setRemindTask(t); setRemindAt(""); }}
            onNav={nav}
          />
        )}

        {/* Completed toggle */}
        <div className="flex items-center justify-between pt-2">
          <Button data-testid="toggle-completed-btn" variant="ghost" size="sm" onClick={() => setShowCompleted((s) => !s)} className="text-slate-500 hover:text-slate-900">
            {showCompleted ? "Hide" : "Show"} completed ({buckets.done.length})
          </Button>
          <div className="text-xs text-slate-400 stamp-label">{totalOpen} open · {isFetching ? "refreshing…" : "up to date"}</div>
        </div>

        {showCompleted && buckets.done.length > 0 && (
          <TaskSection
          onOpen={setSelTask}
            testid="section-done"
            title="Completed"
            icon={CheckSquare}
            tone="emerald"
            tasks={buckets.done}
            onToggle={toggle.mutate}
            onPriority={reprioritize.mutate}
            onDelete={del.mutate}
          onRemind={remind.mutate}
          onCustom={(t) => { setRemindTask(t); setRemindAt(""); }}
            onNav={nav}
          />
        )}
      </div>

      {/* Custom reminder dialog */}
      <Dialog open={!!remindTask} onOpenChange={(o) => !o && setRemindTask(null)}>
        <DialogContent className="rounded-sm sm:max-w-sm" data-testid="custom-reminder-dialog">
          <DialogHeader><DialogTitle className="font-display text-lg flex items-center gap-2"><AlarmClock size={16} className="text-cyan-600" /> New reminder</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600 -mt-2 truncate">{remindTask?.title}</p>
          <Input type="datetime-local" value={remindAt} onChange={(e) => setRemindAt(e.target.value)} className="rounded-sm h-11" data-testid="custom-reminder-input" />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRemindTask(null)} className="rounded-sm">Cancel</Button>
            <Button data-testid="custom-reminder-save-btn" disabled={!remindAt}
              onClick={() => { remind.mutate({ id: remindTask.id, due_at: new Date(remindAt).toISOString() }); setRemindTask(null); }}
              className="rounded-sm bg-cyan-600 hover:bg-cyan-700 text-white">
              Set reminder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ActivityDetailDialog activity={selTask} open={!!selTask} onClose={() => setSelTask(null)} />
    </div>
  );
}

const TONE_CLASSES = {
  rose: { border: "border-l-rose-500", icon: "text-rose-600", bg: "bg-rose-50", text: "text-rose-800" },
  cyan: { border: "border-l-cyan-500", icon: "text-cyan-600", bg: "bg-cyan-50", text: "text-cyan-800" },
  sky: { border: "border-l-sky-500", icon: "text-sky-600", bg: "bg-sky-50", text: "text-sky-800" },
  slate: { border: "border-l-slate-400", icon: "text-slate-500", bg: "bg-slate-50", text: "text-slate-700" },
  emerald: { border: "border-l-emerald-500", icon: "text-emerald-600", bg: "bg-emerald-50", text: "text-emerald-800" },
};

function StatCard({ label, value, tone = "slate", Icon, testid }) {
  const t = TONE_CLASSES[tone];
  return (
    <Card data-testid={testid} className={`rounded-sm p-4 industrial-shadow border-l-4 ${t.border}`}>
      <div className="flex items-center gap-3">
        <Icon className={t.icon} size={22} />
        <div>
          <div className="stamp-label text-slate-500">{label}</div>
          <div className="font-display font-bold text-2xl mt-0.5">{value}</div>
        </div>
      </div>
    </Card>
  );
}

function TaskSection({ title, icon: Icon, tone, tasks, emptyText, onToggle, onPriority, onDelete, onNav, onRemind, onCustom, onOpen, testid }) {
  const t = TONE_CLASSES[tone];
  if (!tasks.length && !emptyText) return null;
  return (
    <Card data-testid={testid} className={`rounded-sm industrial-shadow overflow-hidden border-l-4 ${t.border}`}>
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
        <Icon size={15} className={t.icon} />
        <div className="font-display font-semibold text-slate-800">{title}</div>
        <Badge variant="outline" className={`rounded-sm text-[10px] ${t.bg} ${t.text} border-current`}>{tasks.length}</Badge>
      </div>
      {tasks.length === 0 ? (
        <div className="p-8 text-center text-sm text-slate-400">{emptyText}</div>
      ) : (
        <div className="divide-y divide-slate-100">
          {tasks.map((task) => (
            <TaskRow key={task.id} task={task} onToggle={onToggle} onPriority={onPriority} onDelete={onDelete} onNav={onNav} onRemind={onRemind} onCustom={onCustom} onOpen={onOpen} />
          ))}
        </div>
      )}
    </Card>
  );
}

function TaskRow({ task, onToggle, onPriority, onDelete, onNav, onRemind, onCustom, onOpen }) {
  const TypeIcon = ICON[task.type] || CheckSquare;
  const overdue = isOverdue(task);
  const linkedTo = task.deal_id || task.company_id;
  return (
    <div
      className="flex items-start md:items-center gap-2.5 md:gap-3 px-3 md:px-4 py-3 hover:bg-cyan-50/30 transition-colors group"
      data-testid={`task-row-${task.id}`}
    >
      <Checkbox
        checked={task.completed}
        onCheckedChange={(v) => onToggle({ id: task.id, completed: !!v })}
        className="mt-1 md:mt-0 shrink-0"
        data-testid={`task-toggle-${task.id}`}
      />
      <div className={`w-8 h-8 flex items-center justify-center shrink-0 rounded-sm ${overdue ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600"}`}>
        <TypeIcon size={14} />
      </div>
      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onOpen?.(task)} data-testid={`task-open-${task.id}`}>
        {/* Title — wraps on mobile so long call-back titles don't get cut off */}
        <div className="flex items-start gap-1.5">
          <div className={`w-2 h-2 rounded-full shrink-0 mt-1.5 ${PRIORITY_DOT[task.priority]}`} />
          <div className={`text-sm font-medium leading-tight md:truncate ${task.completed ? "line-through text-slate-500" : "text-slate-900"}`}>
            {task.title}
          </div>
        </div>
        {/* Meta row — wraps on mobile so due-date + badges are all readable */}
        <div className="text-[11px] text-slate-500 flex flex-wrap items-center gap-x-2 gap-y-1 mt-1 pl-3.5">
          {overdue && !task.completed && <AlertCircle size={11} className="text-rose-500" />}
          <span className={overdue && !task.completed ? "text-rose-600 font-medium" : ""}>
            {task.due_at ? fmtDateTime(task.due_at) : "No due date"}
          </span>
          {task.type !== "task" && <Badge variant="outline" className="rounded-sm text-[9px] py-0 h-4 px-1.5">{task.type}</Badge>}
          {/* iter-62: booking-derived activities get a specific badge naming the
              booking type; everything else auto-generated stays as the generic
              AUTO chip. */}
          {task.booking_id ? (
            <Badge
              variant="outline"
              className="rounded-sm text-[9px] py-0 h-4 px-1.5 border-cyan-300 bg-cyan-50 text-cyan-800 inline-flex items-center gap-1"
              data-testid={`task-booking-badge-${task.id}`}
            >
              <CalendarClock size={9} />
              Booking{task.booking_type_label ? ` · ${task.booking_type_label}` : ""}
            </Badge>
          ) : (
            task.auto_generated && <Badge variant="outline" className="rounded-sm text-[9px] py-0 h-4 px-1.5 border-cyan-200 bg-cyan-50 text-cyan-700">AUTO</Badge>
          )}
          {task.assigned_to_name && <Badge variant="outline" className="rounded-sm text-[9px] py-0 h-4 px-1.5 border-slate-300 bg-slate-50 text-slate-600"><User size={9} className="mr-0.5" />{task.assigned_to_name}</Badge>}
          {linkedTo && (
            <div className="flex items-center gap-2">
              {task.deal_id && <button onClick={(e) => { e.stopPropagation(); onNav(`/deals`); }} className="text-cyan-600 hover:underline">Deal</button>}
              {task.company_id && <button onClick={(e) => { e.stopPropagation(); onNav(`/clients/${task.company_id}`); }} className="text-cyan-600 hover:underline">Client</button>}
            </div>
          )}
        </div>
      </div>
      <Badge variant="outline" className={`rounded-sm text-[9px] uppercase font-semibold ${PRIORITY_BADGE[task.priority]} hidden md:inline-flex shrink-0`}>{task.priority}</Badge>
      <Select value={task.priority} onValueChange={(v) => onPriority({ id: task.id, priority: v })}>
        <SelectTrigger data-testid={`task-priority-${task.id}`} className="h-8 w-[96px] rounded-sm text-[10px] uppercase tracking-tight font-semibold md:hidden shrink-0 px-2">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="high">🔴 High</SelectItem>
          <SelectItem value="medium">🟡 Medium</SelectItem>
          <SelectItem value="low">⚪ Low</SelectItem>
        </SelectContent>
      </Select>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="ghost" className="rounded-sm h-8 w-8 p-0 text-slate-400 hover:text-cyan-600 shrink-0" title="Task options" data-testid={`task-options-${task.id}`}>
            <AlarmClock size={14} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="rounded-sm w-52">
          {!task.completed && (
            <>
              <DropdownMenuItem onClick={() => onToggle({ id: task.id, completed: true })} data-testid={`task-complete-${task.id}`}>
                <CheckSquare size={13} className="mr-2 text-emerald-600" /> Mark completed
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-400">Set new reminder</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => onRemind({ id: task.id, due_at: inHours(1) })} data-testid={`task-remind-1h-${task.id}`}>In 1 hour</DropdownMenuItem>
          <DropdownMenuItem onClick={() => onRemind({ id: task.id, due_at: todayAt(17) })}>This evening (5pm)</DropdownMenuItem>
          <DropdownMenuItem onClick={() => onRemind({ id: task.id, due_at: daysAt(1, 9) })} data-testid={`task-remind-tomorrow-${task.id}`}>Tomorrow 9am</DropdownMenuItem>
          <DropdownMenuItem onClick={() => onRemind({ id: task.id, due_at: daysAt(7, 9) })}>Next week</DropdownMenuItem>
          <DropdownMenuItem onClick={() => onCustom(task)} data-testid={`task-remind-custom-${task.id}`}>Pick date &amp; time…</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => window.confirm("Delete this task?") && onDelete(task.id)}
        className="rounded-sm h-8 w-8 p-0 text-slate-400 hover:text-rose-600 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity shrink-0"
        data-testid={`task-delete-${task.id}`}
      >
        <Trash2 size={13} />
      </Button>
    </div>
  );
}
